import { Link } from 'react-router-dom';

export interface AccessDeniedPageProps {
  /** The role that would have been required for the destination the visitor tried to reach. Optional —
   * no caller in this checkout supplies it yet (see this file's own top-of-module doc comment). */
  requiredRoleName?: string;
  /** The email of the currently signed-in user, if any — lets the message say "signed in as ___" rather
   * than a generic "you." */
  signedInAsEmail?: string;
}

/**
 * Presentation-only. Deliberately built as a reusable component that takes props rather than reading
 * anything from context/routing itself, so any future route guard can render it with the specific
 * role/destination it denied — but no such guard exists yet.
 *
 * BACKEND/ARCHITECTURE GAP (documented, intentionally out of EPN-34's scope): there is no established
 * frontend mechanism anywhere in App.tsx today for "this destination requires a specific role/permission
 * the current user lacks" — every route in App.tsx either requires only a signed-in session or is fully
 * public; none are role-gated. This component and its `/access-denied` route exist so a future guard has
 * somewhere real to render, but nothing in this app currently navigates here automatically. Wiring an
 * actual trigger requires deciding which existing 403 responses (across which module gateways) should
 * route here, which is a decision beyond "implement the Login Security states" and is left for that
 * follow-up work.
 *
 * The "Request Access" action is deliberately disabled/inert, per the approved decision: no fake
 * submission and no audit-only pseudo-request — EPN-78 owns the real workflow.
 */
export function AccessDeniedPage({ requiredRoleName, signedInAsEmail }: AccessDeniedPageProps) {
  return (
    <div className="page page--centered">
      <section className="card" data-testid="access-denied-page">
        <h1>Access Denied</h1>
        <p>
          {signedInAsEmail ? (
            <>
              You&rsquo;re signed in as <strong>{signedInAsEmail}</strong>, but you don&rsquo;t have permission to
              view this page.
            </>
          ) : (
            "You don't have permission to view this page."
          )}
          {requiredRoleName && (
            <>
              {' '}
              This destination requires the <strong>{requiredRoleName}</strong> role.
            </>
          )}
        </p>
        <p className="muted">
          If you believe this is a mistake, contact a company administrator who can review your role and
          permissions.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" disabled title="Coming soon" aria-describedby="access-denied-request-caption">
            Request Access
          </button>
          <span id="access-denied-request-caption" className="muted" style={{ fontSize: 12 }}>
            Coming soon
          </span>
        </div>
        <p>
          <Link to="/dashboard">Go to Dashboard</Link>
        </p>
      </section>
    </div>
  );
}
